import {
  type AgentSubject,
  eventsGoalAllowed,
  goalTypeLabel,
  goalTypeSubject,
  isEventsGoalType,
} from "@/shared/domain/agents";
import { type EventModel } from "@/shared/domain/orgs";
import {
  FileText,
  Flag,
  type LucideIcon,
  Tags,
} from "@/shared/foundation/icons";

import { type EditorSubject } from "../../Agents/buildRequestTemplate";
import { type GoalKind } from "../agentFormSchema";

/** The kinds of goal on offer, in the order the picker shows them. */
export const GOAL_KINDS: readonly GoalKind[] = [
  "dataset_summary",
  "dataset_triage",
  "create_events",
  "session_summary",
  "session_triage",
  "session_create_events",
];

/**
 * The kinds the picker offers an agent that runs on `subject`, in an org on
 * `eventModel`: every kind that works on that record and does not create
 * events, and its events goal when the org's event model allows it. No events
 * goal is offered while the event model is still loading, and no goal at all
 * to an agent that runs on nothing specific or has nothing picked yet.
 */
export function offeredGoalKinds(
  subject: EditorSubject | null,
  eventModel: EventModel | undefined,
): GoalKind[] {
  return GOAL_KINDS.filter(
    (kind) =>
      goalTypeSubject(kind) === subject &&
      (!isEventsGoalType(kind) ||
        (eventModel !== undefined && eventsGoalAllowed(kind, eventModel))),
  );
}

/**
 * What a goal added from the button is until its own picker says otherwise:
 * the summary of the record the agent runs on, which needs nothing filled in
 * to be valid.
 */
export const DEFAULT_GOAL_KINDS: Record<AgentSubject, GoalKind> = {
  dataset: "dataset_summary",
  session: "session_summary",
};

/** How a kind of goal is named wherever one is shown. */
export interface GoalKindCopy {
  label: string;
  /** One line on what a goal of this kind does. */
  description: string;
  icon: LucideIcon;
}

/**
 * The form's picker and the agent's own page both name goals from this, so a
 * kind reads the same while it is written and once it is saved. The label is
 * {@link goalTypeLabel}'s, which also names goals the editor cannot edit and a
 * thread's goal chips.
 */
export const GOAL_KIND_COPY: Record<GoalKind, GoalKindCopy> = {
  dataset_summary: {
    label: goalTypeLabel("dataset_summary"),
    description: "Write up what the dataset contains",
    icon: FileText,
  },
  dataset_triage: {
    label: goalTypeLabel("dataset_triage"),
    description: "Tag the dataset from a vocabulary you set",
    icon: Tags,
  },
  create_events: {
    label: goalTypeLabel("create_events"),
    description: "Mark moments in the dataset from a vocabulary you set",
    icon: Flag,
  },
  session_summary: {
    label: goalTypeLabel("session_summary"),
    description: "Write up what the session contains",
    icon: FileText,
  },
  session_triage: {
    label: goalTypeLabel("session_triage"),
    description: "Tag the session from a vocabulary you set",
    icon: Tags,
  },
  session_create_events: {
    label: goalTypeLabel("session_create_events"),
    description: "Mark moments in the session from a vocabulary you set",
    icon: Flag,
  },
};
